import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { SkeletonRows } from '../../components/skeleton'
import { getPlans, createPlan, updatePlan, deletePlan } from '../../services/platform'
import { onQuerySuccess, onMutateSuccess } from '../../utils/queryResponse'
import refreshPlatform from './refreshPlatform'
import { toast } from '../../utils/toast'
import PlatformConfirm from './platformConfirm'

// The form edits exactly what the API stores on a Plan: name, price (per
// month), trialDays and description. trialDays matters beyond display —
// assigning the plan to a company sets that company's trial end from it.
const EMPTY_FORM = { name: '', price: '', trialDays: '', description: '' }

// Prisma sends the Decimal price as a string ("29.99") or null.
const formatPrice = (price) => price != null && price !== '' ? `$${Number(price).toFixed(2)}` : '—'

const PlatformPlansPage = () => {
    const { t } = useTranslation()
    const qc = useQueryClient()
    const [plans, setPlans] = useState([])
    const [showModal, setShowModal] = useState(false)
    const [editingPlan, setEditingPlan] = useState(null)
    const [confirm, setConfirm] = useState(null)
    const [form, setForm] = useState(EMPTY_FORM)

    const { isFetching, data } = useQuery('platform-plans', getPlans)

    useEffect(() => { onQuerySuccess(data, (r) => setPlans(r.plans || r || [])) }, [data])

    const createMut = useMutation(createPlan, { onSuccess: onMutateSuccess(() => { refreshPlatform(qc); closeModal() }, t('platform.plans.createdToast')) })
    const updateMut = useMutation(({ id, payload }) => updatePlan(id, payload), { onSuccess: onMutateSuccess(() => { refreshPlatform(qc); closeModal() }, t('platform.plans.updatedToast')) })
    const deleteMut = useMutation(deletePlan, { onSuccess: onMutateSuccess(() => refreshPlatform(qc), t('platform.plans.deletedToast')) })

    const closeModal = () => { setShowModal(false); setEditingPlan(null); setForm(EMPTY_FORM) }

    const openCreate = () => { setForm(EMPTY_FORM); setEditingPlan(null); setShowModal(true) }

    const openEdit = (p) => {
        setForm({
            name: p.name || '',
            price: p.price != null ? String(Number(p.price)) : '',
            trialDays: p.trialDays ? String(p.trialDays) : '',
            description: p.description || '',
        })
        setEditingPlan(p)
        setShowModal(true)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const name = form.name.trim()
        if (!name) return toast.error(t('platform.plans.nameRequired'))

        const price = form.price === '' ? null : Number(form.price)
        const trialDays = form.trialDays === '' ? 0 : Math.round(Number(form.trialDays))
        const description = form.description.trim()

        if (editingPlan) {
            // Update accepts null to clear the price / description.
            updateMut.mutate({ id: editingPlan.id, payload: { name, price, trialDays, description: description || null } })
        } else {
            // Create takes them as optional (not null) — leave out what's empty.
            createMut.mutate({ name, trialDays, ...(price !== null && { price }), ...(description && { description }) })
        }
    }

    // Delete waits for the confirmation modal.
    const handleDelete = (p) => setConfirm({ action: 'deletePlan', id: p.id, name: p.name })

    const trialText = (p) => p.trialDays > 0 ? t('platform.plans.days', { count: p.trialDays }) : t('platform.plans.noTrial')

    const isSaving = createMut.isLoading || updateMut.isLoading

    return (
        <div className="sbr">
            <div className="pl-page-head" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: '#0B1424' }}>{t('platform.plans.title')}</h2>
                    <p style={{ fontSize: 13, color: '#697586', margin: 0 }}>{t('platform.plans.subtitle')}</p>
                </div>
                <button className="btn-sbr btn-primary-sbr" onClick={openCreate} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8 }}>{t('platform.plans.add')}</button>
            </div>

            <div className="card-sbr" style={{ padding: 0 }}>
                {isFetching && plans.length === 0 ? (
                    <div style={{ padding: 20 }}><SkeletonRows rows={4} widths={['25%', '15%', '15%', '15%', '10%']} /></div>
                ) : (
                    <>
                    <table className="p-table p-plans-table">
                        <thead>
                            <tr>
                                <th>{t('platform.columns.planName')}</th>
                                <th>{t('platform.columns.monthly')}</th>
                                <th>{t('platform.columns.trialDays')}</th>
                                <th>{t('platform.columns.companies')}</th>
                                <th style={{ width: 120 }}>{t('platform.columns.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div style={{ fontWeight: 600, color: '#0B1424' }}>{p.name}</div>
                                        {p.description && <div style={{ fontSize: 12, color: '#9CA3B0', marginTop: 2 }}>{p.description}</div>}
                                    </td>
                                    <td style={{ fontSize: 13, color: '#697586' }}>{formatPrice(p.price)}</td>
                                    <td style={{ fontSize: 13, color: '#697586' }}>{trialText(p)}</td>
                                    <td style={{ fontSize: 13, color: '#697586' }}>{p.companyCount ?? 0}</td>
                                    <td>
                                        <div className="p-row-actions" style={{ display: 'flex', gap: 6 }}>
                                            <button className="btn-sbr btn-ghost-sbr" style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6 }} onClick={() => openEdit(p)}>{t('platform.common.edit')}</button>
                                            <button className="btn-sbr btn-danger-sbr" style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6 }} onClick={() => handleDelete(p)} disabled={deleteMut.isLoading}>{t('platform.common.delete')}</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {plans.length === 0 && !isFetching && (
                                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9CA3B0', padding: 32 }}>{t('platform.plans.empty')}</td></tr>
                            )}
                        </tbody>
                    </table>

                    {/* Mobile: one block per plan — price, trial and companies, then Edit / Delete side by side. */}
                    <div className="p-plan-list">
                        {plans.map(p => (
                            <div key={p.id} className="p-plan-item">
                                <div className="p-plan-head">
                                    <div>
                                        <div className="p-plan-name">{p.name}</div>
                                        {p.description && <div className="p-plan-desc">{p.description}</div>}
                                    </div>
                                    <div className="p-plan-price">
                                        <b>{formatPrice(p.price)}</b>
                                        <span>{t('platform.columns.monthly')}</span>
                                    </div>
                                </div>
                                <div className="p-plan-meta">
                                    <div><div className="k">{t('platform.columns.trialDays')}</div><div className="v">{trialText(p)}</div></div>
                                    <div><div className="k">{t('platform.columns.companies')}</div><div className="v">{p.companyCount ?? 0}</div></div>
                                </div>
                                <div className="p-plan-actions">
                                    <button className="btn-sbr btn-ghost-sbr" onClick={() => openEdit(p)}>{t('platform.common.edit')}</button>
                                    <button className="btn-sbr btn-danger-sbr" onClick={() => handleDelete(p)} disabled={deleteMut.isLoading}>{t('platform.common.delete')}</button>
                                </div>
                            </div>
                        ))}
                        {plans.length === 0 && !isFetching && (
                            <div style={{ textAlign: 'center', color: '#9CA3B0', padding: 32 }}>{t('platform.plans.empty')}</div>
                        )}
                    </div>
                    </>
                )}
            </div>

            <PlatformConfirm confirm={confirm} onClose={() => setConfirm(null)} onConfirm={(c) => deleteMut.mutate(c.id)} />

            {/* Create / Edit Plan Modal */}
            {showModal && (
                <div className="p-modal-overlay" onClick={closeModal}>
                    <div className="p-modal" onClick={e => e.stopPropagation()}>
                        <h3>{editingPlan ? t('platform.plans.editTitle') : t('platform.plans.createTitle')}</h3>
                        <form onSubmit={handleSubmit}>
                            <label>{t('platform.plans.nameLabel')}</label>
                            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Pro" maxLength={200} />

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div>
                                    <label>{t('platform.plans.monthlyPriceLabel')}</label>
                                    <input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="29.99" />
                                </div>
                                <div>
                                    <label>{t('platform.plans.trialDaysLabel')}</label>
                                    <input type="number" min="0" step="1" value={form.trialDays} onChange={e => setForm(f => ({ ...f, trialDays: e.target.value }))} placeholder="0" />
                                </div>
                            </div>

                            <label>{t('platform.plans.descriptionLabel')}</label>
                            <textarea rows={3} maxLength={500} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('platform.plans.descriptionPlaceholder')} />

                            <div className="p-modal-actions">
                                <button type="button" className="btn-sbr btn-ghost-sbr" onClick={closeModal} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 8 }}>{t('platform.common.cancel')}</button>
                                <button type="submit" className="btn-sbr btn-primary-sbr" disabled={isSaving} style={{ padding: '8px 18px', fontSize: 13, borderRadius: 8 }}>{isSaving ? t('platform.common.saving') : editingPlan ? t('platform.common.update') : t('platform.common.create')}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PlatformPlansPage
